import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Theme definitions with CSS variable mappings
const THEME_DEFINITIONS = {
  standard: {
    name: 'Slate',
    primary: '#0f172a',
    primaryLight: '#1e293b',
    accent: '#10b981',
    sidebar: '#0f172a',
    sidebarHover: '#1e293b',
    button: '#10b981',
    buttonHover: '#059669',
  },
  blue: {
    name: 'Blue',
    primary: '#1e40af',
    primaryLight: '#3b82f6',
    accent: '#3b82f6',
    sidebar: '#1e40af',
    sidebarHover: '#1d4ed8',
    button: '#3b82f6',
    buttonHover: '#2563eb',
  },
  green: {
    name: 'Green',
    primary: '#166534',
    primaryLight: '#22c55e',
    accent: '#22c55e',
    sidebar: '#166534',
    sidebarHover: '#15803d',
    button: '#22c55e',
    buttonHover: '#16a34a',
  },
  orange: {
    name: 'Orange',
    primary: '#c2410c',
    primaryLight: '#f97316',
    accent: '#f97316',
    sidebar: '#c2410c',
    sidebarHover: '#ea580c',
    button: '#f97316',
    buttonHover: '#ea580c',
  },
  purple: {
    name: 'Purple',
    primary: '#6b21a8',
    primaryLight: '#8b5cf6',
    accent: '#8b5cf6',
    sidebar: '#6b21a8',
    sidebarHover: '#7c3aed',
    button: '#8b5cf6',
    buttonHover: '#7c3aed',
  },
  pink: {
    name: 'Pink',
    primary: '#be185d',
    primaryLight: '#ec4899',
    accent: '#ec4899',
    sidebar: '#be185d',
    sidebarHover: '#db2777',
    button: '#ec4899',
    buttonHover: '#db2777',
  },
  red: {
    name: 'Red',
    primary: '#b91c1c',
    primaryLight: '#ef4444',
    accent: '#ef4444',
    sidebar: '#b91c1c',
    sidebarHover: '#dc2626',
    button: '#ef4444',
    buttonHover: '#dc2626',
  },
};

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('standard');
  const [customColor, setCustomColor] = useState('#10b981');
  const [isLoading, setIsLoading] = useState(true);

  // Apply theme CSS variables to document root
  const applyTheme = useCallback((themeId, customHex = null) => {
    const root = document.documentElement;
    
    let theme;
    if (themeId === 'custom' && customHex) {
      // Generate theme from custom color
      theme = {
        primary: customHex,
        primaryLight: adjustColor(customHex, 40),
        accent: customHex,
        sidebar: customHex,
        sidebarHover: adjustColor(customHex, 20),
        button: customHex,
        buttonHover: adjustColor(customHex, -20),
      };
    } else {
      theme = THEME_DEFINITIONS[themeId] || THEME_DEFINITIONS.standard;
    }

    // Apply CSS variables
    root.style.setProperty('--theme-primary', theme.primary);
    root.style.setProperty('--theme-primary-light', theme.primaryLight);
    root.style.setProperty('--theme-accent', theme.accent);
    root.style.setProperty('--theme-sidebar', theme.sidebar);
    root.style.setProperty('--theme-sidebar-hover', theme.sidebarHover);
    root.style.setProperty('--theme-button', theme.button);
    root.style.setProperty('--theme-button-hover', theme.buttonHover);
  }, []);

  // Load saved theme from backend on mount and when token changes
  useEffect(() => {
    const loadSavedTheme = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        if (!token) {
          setIsLoading(false);
          // Apply default theme if no token
          applyTheme('standard', null);
          return;
        }

        const res = await fetch(`${API_URL}/api/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (res.ok) {
          const data = await res.json();
          const savedTheme = data.ui_theme || 'standard';
          const savedCustomColor = data.ui_theme_custom_color || '#10b981';
          
          setCurrentTheme(savedTheme);
          setCustomColor(savedCustomColor);
          applyTheme(savedTheme, savedTheme === 'custom' ? savedCustomColor : null);
        }
      } catch (err) {
        console.error('Error loading theme:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadSavedTheme();
    
    // Listen for storage changes (login/logout from other tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'streakfree_token') {
        loadSavedTheme();
      }
    };
    
    // Listen for custom login event
    const handleLogin = () => {
      loadSavedTheme();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLoggedIn', handleLogin);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLoggedIn', handleLogin);
    };
  }, [applyTheme]);

  // Save theme to backend
  const saveTheme = async (themeId, customHex = null) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      if (!token) return false;

      const payload = {
        ui_theme: themeId,
      };
      
      if (themeId === 'custom' && customHex) {
        payload.ui_theme_custom_color = customHex;
      }

      const res = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      return res.ok;
    } catch (err) {
      console.error('Error saving theme:', err);
      return false;
    }
  };

  // Change theme and persist
  const changeTheme = async (themeId, customHex = null) => {
    setCurrentTheme(themeId);
    if (customHex) {
      setCustomColor(customHex);
    }
    
    // Apply immediately for instant feedback
    applyTheme(themeId, themeId === 'custom' ? (customHex || customColor) : null);
    
    // Save to backend
    await saveTheme(themeId, customHex);
  };

  // Get current theme colors
  const getThemeColors = () => {
    if (currentTheme === 'custom') {
      return {
        primary: customColor,
        primaryLight: adjustColor(customColor, 40),
        accent: customColor,
        sidebar: customColor,
        sidebarHover: adjustColor(customColor, 20),
        button: customColor,
        buttonHover: adjustColor(customColor, -20),
      };
    }
    return THEME_DEFINITIONS[currentTheme] || THEME_DEFINITIONS.standard;
  };

  const value = {
    currentTheme,
    customColor,
    isLoading,
    changeTheme,
    getThemeColors,
    themeDefinitions: THEME_DEFINITIONS,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Helper to adjust color brightness
function adjustColor(color, amount) {
  const clamp = (num) => Math.min(255, Math.max(0, num));
  const hex = color.replace('#', '');
  const num = parseInt(hex, 16);
  const r = clamp((num >> 16) + amount);
  const g = clamp(((num >> 8) & 0x00FF) + amount);
  const b = clamp((num & 0x0000FF) + amount);
  return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`;
}

export default ThemeContext;
